import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormField, provideSignalFormsConfig } from '@angular/forms/signals';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { ErrorModule } from '@data-access/api/error';
import { apolloProvider } from '@data-access/graphql';
import {
  AnalyticsModule,
  CookiesService,
  ErrorTrackingService,
  GlobalErrorHandler,
  HeapService,
} from '@feature/analytics';
import { AuthModule, AuthService } from '@feature/auth';
import { MemberService } from '@feature/member';
import { SharedModule } from '@feature/shared';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { UIDialogConfirmModule, UISnackbarModule } from '@ui/components';
import { EnvironmentService } from '@util/environment';
import { I18nRootModule } from '@util/i18n';
import { provideApollo } from 'apollo-angular';
import { NgxMaskModule } from 'ngx-mask';
import { filter, Observable, switchMap, take, tap } from 'rxjs';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

const APP_INITIALIZER_FACTORY: (
  authService: AuthService,
  memberService: MemberService,
  environmentService: EnvironmentService,
  heapService: HeapService,
  cookiesService: CookiesService,
  errorTrackingService: ErrorTrackingService,
) => () => Observable<boolean> =
  (
    authService: AuthService,
    memberService: MemberService,
    environmentService: EnvironmentService,
    heapService: HeapService,
    cookiesService: CookiesService,
    errorTrackingService: ErrorTrackingService,
  ) =>
  () =>
    environmentService.loadEnvironment$().pipe(
      switchMap(() => environmentService.loadInfo$()),
      tap(() => {
        errorTrackingService.init();
        cookiesService.init();
        heapService.init();
      }),
      switchMap(() => authService.init()),
      switchMap(() => {
        memberService.observeLoggedIn();
        return memberService.initialised$.pipe(
          filter((initialised: boolean) => !!initialised),
          take(1),
        );
      }),
    );

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    IonicModule.forRoot({
      rippleEffect: false,
      animated: true,
    }),
    AppRoutingModule,
    MatSnackBarModule,
    UISnackbarModule,
    AuthModule,
    ErrorModule,
    NgxMaskModule.forRoot(),
    I18nRootModule,
    AnalyticsModule,
    UIDialogConfirmModule,
    SharedModule,
  ],
  providers: [
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        verticalPosition: 'bottom',
      },
    },
    { provide: AuthService },
    provideAppInitializer(() => {
      const initializerFn = APP_INITIALIZER_FACTORY(
        inject(AuthService),
        inject(MemberService),
        inject(EnvironmentService),
        inject(HeapService),
        inject(CookiesService),
        inject(ErrorTrackingService),
      );
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideApollo(apolloProvider),
    provideSignalFormsConfig({
      classes: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-touched': (state: FormField<unknown>): boolean => state.state().touched(),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'ng-invalid': (state: FormField<unknown>): boolean => state.state().invalid(),
      },
    }),
  ],
})
export class AppModule {}
